import { getDomainId } from "./domains";
import { fetchCCTPMessage } from "./fetch";
import { checkMessageClaimed } from "./nonce";
import { type CCTPVersion, getCCTPTransferFields } from "./version";

export interface CCTPLookupResult {
  messageBytes: string;
  attestation: string | null;
  amount: number;
  sourceChain: string | null;
  status: "pending" | "complete";
  cctpVersion: CCTPVersion;
}

/**
 * Looks up a CCTP message from the Circle API by source domain and tx hash.
 * Returns the message bytes, attestation (if ready), and parsed USDC amount.
 * Returns null if Circle hasn't seen the transaction (404), and `"not_attested"` if it has but
 * hasn't published the message bytes yet, which scans must come back for.
 */
export async function lookupCCTPMessage(
  domainId: number,
  txHash: string,
): Promise<CCTPLookupResult | "not_attested" | null> {
  const isHex = /^(0x)?[0-9a-fA-F]{64}$/.test(txHash);
  const isBase58 = /^[1-9A-HJ-NP-Za-km-z]{32,88}$/.test(txHash);
  if (!isHex && !isBase58) return null;

  const data = await fetchCCTPMessage({ domainId, txHash });
  if (!data) return null;
  if (!data.message) return "not_attested";

  const fields = getCCTPTransferFields(data.message);
  if (!fields) return null;

  const attestation = data.status === "complete" ? data.attestation : null;

  return {
    messageBytes: data.message,
    attestation,
    amount: fields.amount,
    sourceChain: fields.sourceChain,
    status: attestation ? "complete" : "pending",
    cctpVersion: fields.version,
  };
}

/**
 * Normalizes an address for comparison by removing the 0x prefix, stripping
 * leading zeros, and lowercasing. Handles both EVM (20-byte) and Aptos
 * (32-byte with variable leading zeros) address formats.
 */
function normalizeAddress(address: string): string {
  const stripped = address.startsWith("0x") ? address.slice(2) : address;
  return stripped.replace(/^0+/, "").toLowerCase() || "0";
}

/** Result of a successful CCTP transfer validation (not redeemed, destined for the expected recipient). */
export interface ValidatedCCTPTransfer {
  result: CCTPLookupResult;
  mintRecipient: string;
}

/**
 * Looks up a CCTP transaction via Circle API and validates it is:
 * 1. Addressed to the expected recipient
 * 2. Not already redeemed on the destination chain
 *
 * Handles both deposits (-> Aptos) and withdrawals (-> EVM) by checking
 * the nonce on the appropriate destination chain.
 *
 * Returns the validated transfer data, or `"redeemed"` / `null` if the tx is
 * already claimed or fails validation respectively.
 */
export async function validateCCTPTransfer(
  domainId: number,
  txHash: string,
  expectedRecipientAddress: string,
): Promise<ValidatedCCTPTransfer | "redeemed" | "not_attested" | null> {
  const result = await lookupCCTPMessage(domainId, txHash);
  if (!result || result === "not_attested") return result;

  const fields = getCCTPTransferFields(result.messageBytes);
  if (!fields) return null;

  const normalizedRecipient = normalizeAddress(fields.mintRecipient);
  const normalizedExpected = normalizeAddress(expectedRecipientAddress);
  if (normalizedRecipient !== normalizedExpected) return null;

  const claimed = await checkMessageClaimed(result.messageBytes);
  if (claimed === "unknown") return null;
  if (claimed === "claimed") return "redeemed";

  return { result, mintRecipient: fields.mintRecipient };
}

// ---------------------------------------------------------------------------
// High-level lookup by chain name + tx hash (used by resume modals)
// ---------------------------------------------------------------------------

export type CCTPLookupError = "invalid_format" | "unsupported_chain" | "not_found" | "api_error";

/**
 * Looks up a CCTP transfer by chain name and tx hash, returning structured
 * errors instead of null so callers can display specific error messages.
 */
export async function lookupCCTPTransfer(
  chainName: string,
  txHash: string,
): Promise<
  | { result: CCTPLookupResult; error?: never; cause?: never }
  | { result?: never; error: CCTPLookupError; cause?: unknown }
> {
  const trimmed = txHash.trim();
  if (!trimmed) return { error: "invalid_format" };

  const isHex = /^(0x)?[0-9a-fA-F]{64}$/.test(trimmed);
  const isBase58 = /^[1-9A-HJ-NP-Za-km-z]{32,88}$/.test(trimmed);
  if (!isHex && !isBase58) return { error: "invalid_format" };

  const domainId = getDomainId(chainName);
  if (domainId === null) return { error: "unsupported_chain" };

  try {
    const result = await lookupCCTPMessage(domainId, trimmed);
    if (!result || result === "not_attested") return { error: "not_found" };
    return { result };
  } catch (err) {
    return { error: "api_error", cause: err };
  }
}
